import React, {useState} from 'react';
import {Divider, Drawer, IconButton, List, ListItemButton, ListItemText, Menu, MenuItem, Typography} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import type {DocsManifestEntry} from '@/types/docsManifest';
import type {VideosManifestEntry} from '@/types/videosManifest';
import DocViewerDialog from './DocViewerDialog';
import VideoPlayerDialog from './VideoPlayerDialog';
import {useDocsCatalog} from './useDocsCatalog';
import {useVideosCatalog} from './useVideosCatalog';
import {getPageLabelFromPathname} from './videosMenuUtils';
import {useIsMobile} from '@/hooks/useIsMobile';
import './DocsMenu.css';

interface DocsMenuTutorialProps {
    /** Open the full app tutorial. */
    onStartFull: () => void;
    /** Optional per-page tutorial. Omit to hide the "page tutorial" entry. */
    page?: { onStart: () => void };
}

interface DocsMenuProps {
    /** Active roles used to filter role-scoped manuals. Defaults to []. */
    activeRoles?: string[];
    /**
     * Translated label for the current page (e.g. "Atoms"). Used in the
     * "{{page}} page tutorial" entry. If null/undefined, falls back to the
     * route's first segment.
     */
    pageLabel?: string | null;
    /** Optional tutorial integration. Omit to skip tutorial menu entries. */
    tutorial?: DocsMenuTutorialProps;
}

const DocsMenu: React.FC<DocsMenuProps> = ({activeRoles = [], pageLabel, tutorial}) => {
    const {t} = useTranslation();
    const {pathname} = useLocation();
    const {loaded, sections, selectedDoc, openDoc, closeDoc} = useDocsCatalog(activeRoles, pathname);
    const {pageVideo, selectedVideo, openVideo, closeVideo} = useVideosCatalog(pathname);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const isMobile = useIsMobile();

    if (!loaded) return null;

    const effectivePageLabel = pageLabel ?? getPageLabelFromPathname(pathname);
    const showFullTutorial = !!tutorial;
    const showPageTutorial = !!tutorial?.page;
    const showPageVideo = !!pageVideo;
    const showGuides = showFullTutorial || showPageTutorial || showPageVideo;

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        if (isMobile) {
            setSheetOpen(true);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSheetOpen(false);
    };

    const handleClick = (entry: DocsManifestEntry) => {
        openDoc(entry);
        handleClose();
    };

    const handleVideoClick = (entry: VideosManifestEntry) => {
        openVideo(entry);
        handleClose();
    };

    const handlePageVideoClick = () => {
        if (pageVideo) handleVideoClick(pageVideo);
    };

    const handleStartFullTour = () => {
        handleClose();
        tutorial?.onStartFull();
    };

    const handleStartPageTour = () => {
        handleClose();
        tutorial?.page?.onStart();
    };

    const menuSectionItems = sections.flatMap((section, index) => {
        const items = [
            <MenuItem key={`${section.key}-label`} disabled dense>
                <Typography variant="caption" sx={{fontWeight: 700, textTransform: 'uppercase'}}>
                    {t(section.labelKey)}
                </Typography>
            </MenuItem>,
            ...section.entries.map(entry => (
                <MenuItem key={entry.file} onClick={() => handleClick(entry)}>
                    <ListItemText primary={entry.title}/>
                </MenuItem>
            )),
        ];

        return index === 0 ? items : [<Divider key={`${section.key}-divider`}/>, ...items];
    });

    return (
        <>
            <IconButton
                color="inherit"
                onClick={handleOpen}
                aria-label={t('header.help')}
                data-tutorial="docs-menu"
            >
                <HelpOutlineIcon/>
            </IconButton>

            {/* Desktop: dropdown menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                <MenuItem disabled>
                    <Typography variant="subtitle2">{t('header.help')}</Typography>
                </MenuItem>
                {showGuides && (
                    <MenuItem disabled dense>
                        <Typography variant="caption" sx={{fontWeight: 700, textTransform: 'uppercase'}}>
                            {t('header.interactiveGuides')}
                        </Typography>
                    </MenuItem>
                )}
                {showFullTutorial && (
                    <MenuItem onClick={handleStartFullTour}>
                        <ExploreOutlinedIcon fontSize="small" style={{marginRight: 8}}/>
                        <ListItemText primary={t('tutorial.menu.fullTutorial')}/>
                    </MenuItem>
                )}
                {showPageTutorial && (
                    <MenuItem onClick={handleStartPageTour}>
                        <PlayCircleOutlineIcon fontSize="small" style={{marginRight: 8}}/>
                        <ListItemText
                            primary={t('tutorial.menu.pageTutorial', {page: effectivePageLabel})}
                        />
                    </MenuItem>
                )}
                {showPageVideo && (
                    <MenuItem onClick={handlePageVideoClick}>
                        <OndemandVideoIcon fontSize="small" style={{marginRight: 8}}/>
                        <ListItemText
                            primary={pageVideo?.title}
                        />
                    </MenuItem>
                )}
                {sections.length > 0 && <Divider/>}
                {menuSectionItems}
            </Menu>

            {/* Mobile: bottom sheet */}
            <Drawer
                anchor="bottom"
                open={sheetOpen}
                onClose={handleClose}
                // Lift above the fixed BottomNav (z-index 1200, same as the default
                // drawer level) so iOS can't paint the nav over the sheet's lower rows.
                sx={{zIndex: (theme) => theme.zIndex.modal}}
                slotProps={{paper: {className: 'docs-bottom-sheet'}}}
            >
                <div className="docs-bottom-sheet-handle-bar" aria-hidden="true">
                    <span className="docs-bottom-sheet-handle"/>
                </div>
                <div className="docs-bottom-sheet-header">
                    <Typography component="h2" className="docs-bottom-sheet-title">
                        {t('header.help')}
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        aria-label={t('common.close')}
                        className="docs-bottom-sheet-close"
                    >
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </div>
                <div className="docs-bottom-sheet-content">
                    {showGuides && (
                        <div className="docs-bottom-sheet-section-label">
                            {t('header.interactiveGuides')}
                        </div>
                    )}
                    <List disablePadding>
                        {showFullTutorial && (
                            <ListItemButton onClick={handleStartFullTour} className="docs-bottom-sheet-item">
                                <ExploreOutlinedIcon fontSize="small" style={{marginRight: 12}}/>
                                <ListItemText
                                    primary={t('tutorial.menu.fullTutorial')}
                                    slotProps={{primary: {className: 'docs-bottom-sheet-item-text'}}}
                                />
                                <ChevronRightIcon className="docs-bottom-sheet-item-chevron"/>
                            </ListItemButton>
                        )}
                        {showPageTutorial && (
                            <ListItemButton onClick={handleStartPageTour} className="docs-bottom-sheet-item">
                                <PlayCircleOutlineIcon fontSize="small" style={{marginRight: 12}}/>
                                <ListItemText
                                    primary={t('tutorial.menu.pageTutorial', {page: effectivePageLabel})}
                                    slotProps={{primary: {className: 'docs-bottom-sheet-item-text'}}}
                                />
                                <ChevronRightIcon className="docs-bottom-sheet-item-chevron"/>
                            </ListItemButton>
                        )}
                        {showPageVideo && (
                            <ListItemButton onClick={handlePageVideoClick} className="docs-bottom-sheet-item">
                                <OndemandVideoIcon fontSize="small" style={{marginRight: 12}}/>
                                <ListItemText
                                    primary={pageVideo?.title}
                                    slotProps={{primary: {className: 'docs-bottom-sheet-item-text'}}}
                                />
                                <ChevronRightIcon className="docs-bottom-sheet-item-chevron"/>
                            </ListItemButton>
                        )}
                    </List>
                    {sections.map((section) => (
                        <React.Fragment key={section.key}>
                            <div className="docs-bottom-sheet-divider"/>
                            <div className="docs-bottom-sheet-section-label">
                                {t(section.labelKey)}
                            </div>
                            <List disablePadding>
                                {section.entries.map(entry => (
                                    <ListItemButton
                                        key={entry.file}
                                        onClick={() => handleClick(entry)}
                                        className="docs-bottom-sheet-item"
                                    >
                                        <ListItemText
                                            primary={entry.title}
                                            slotProps={{primary: {className: 'docs-bottom-sheet-item-text'}}}
                                        />
                                        <ChevronRightIcon className="docs-bottom-sheet-item-chevron"/>
                                    </ListItemButton>
                                ))}
                            </List>
                        </React.Fragment>
                    ))}
                    <div className="docs-bottom-sheet-safe-area"/>
                </div>
            </Drawer>

            <DocViewerDialog selectedDoc={selectedDoc} onClose={closeDoc}/>
            <VideoPlayerDialog selectedVideo={selectedVideo} onClose={closeVideo}/>
        </>
    );
};

export default DocsMenu;
